import { z } from 'zod';

/**
 * Recursive query-tree types for the feed-filter query builder.
 *
 * **Shared by both portals.** The consumer edits MLS feed configs
 * (`features/mls-feed-config`); the provider edits data-feed filters
 * (`features/data-feed-filters`). Both screens are the same query builder over the
 * same wire format, against different endpoints — so this shape lives here rather
 * than once per portal. Behaviour over these types is in `@realtyfeed/utils`
 * (`treeToFlat` / `flatToTree`) and validation in `query-builder.schema.ts`.
 *
 * Ported from the ad-hoc, untyped (`any`) node shapes used throughout
 * `src(IDX)/components/MlsFeedConfig/Edit/` (Edit/index.tsx +
 * `Queries/{SimpleQuery,OperatorQuery,GroupQuery,InnerGroupQuery}.tsx`). The
 * legacy code represented a flat Formik array (`values.allConfigs`) mixing
 * three node "kinds" — simple query / logical relation / group — where a
 * group's own `value` array could recurse one further level into an
 * "innerGroup". This file makes that implicit shape explicit and typed.
 *
 * Nesting is capped at exactly the two levels the legacy UI/parsing logic
 * supported (top level -> group -> innerGroup); the legacy `getConfigs`
 * parser (`Edit/index.tsx`) never recursed past `innerGroup`, so
 * `InnerGroupNode` cannot itself contain a further group.
 */

export type SimpleQueryNode = {
  type: 'simple';
  field: string | null;
  operator: string | null;
  value: string;
};

export type RelationNode = {
  type: 'relation';
  op: 'and' | 'or' | null;
};

export type InnerGroupNode = {
  type: 'innerGroup';
  value: (SimpleQueryNode | RelationNode)[];
};

export type GroupNode = {
  type: 'group';
  value: (SimpleQueryNode | RelationNode | InnerGroupNode)[];
};

/** A node at the top level of the query builder (`allConfigs[number]`). */
export type QueryNode = SimpleQueryNode | RelationNode | GroupNode;

/** A node one level inside a `GroupNode`'s `value` array. */
export type GroupChildNode = SimpleQueryNode | RelationNode | InnerGroupNode;

export type QueryBuilderFormValues = {
  allConfigs: QueryNode[];
  /** Display-only; not submitted. Populated from the fetched config's `mls_name`. */
  mlsName?: string;
};

export type QueryFieldOption = {
  value: string;
  label: string;
};

/**
 * One resource's worth of the field catalogue behind the builder's Field
 * dropdown - `GET /listing/listing-fields?mls_id=`.
 *
 * **Shared by both portals**, which is why it lives here: the provider builds a
 * data-feed filter over its own MLS's fields, and the consumer builds its feed
 * config over the same catalogue for whichever MLS it is configuring. The
 * response is grouped by resource; `useListingFieldOptions` flattens it.
 */
export type ListingFieldGroup = {
  fields?: ListingField[] | null;
};

export type ListingField = {
  Name: string;
  /** Falsy means the MLS does not expose the field; it must not be offered. */
  Status?: boolean | null;
  Annotation?: { String?: string | null } | null;
};

/**
 * Validation for the recursive query-builder tree (see the node types above).
 *
 * **Shared by both portals** — see the note at the top of this file.
 *
 * Replaces the legacy `handleFormSubmit`'s hand-rolled, deeply-nested
 * `setFieldError` walk in `Edit/index.tsx` (three near-identical `forEach`
 * blocks for top-level / group / innerGroup nodes). Zod expresses the same
 * "a simple query needs a field, an operator, and a non-empty value" rule
 * once per node kind and RHF's `zodResolver` handles surfacing the errors at
 * the right nested path automatically.
 */
const relationSchema = z.object({
  type: z.literal('relation'),
  op: z.enum(['and', 'or']).nullable(),
});

const simpleQuerySchema = z
  .object({
    type: z.literal('simple'),
    field: z.string().nullable(),
    operator: z.string().nullable(),
    value: z.string(),
  })
  .superRefine((node, ctx) => {
    if (!node.field) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Field is required', path: ['field'] });
    }
    if (!node.operator) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Operator is required',
        path: ['operator'],
      });
    }
    if (!node.value || node.value.trim() === '') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Please enter value', path: ['value'] });
    }
  });

const innerGroupSchema = z.object({
  type: z.literal('innerGroup'),
  value: z.array(z.union([simpleQuerySchema, relationSchema])),
});

const groupSchema = z.object({
  type: z.literal('group'),
  value: z.array(z.union([simpleQuerySchema, relationSchema, innerGroupSchema])),
});

const queryNodeSchema = z.union([simpleQuerySchema, relationSchema, groupSchema]);

export const queryBuilderSchema = z.object({
  allConfigs: z.array(queryNodeSchema),
  mlsName: z.string().optional(),
});

export type QueryBuilderSchemaValues = z.infer<typeof queryBuilderSchema>;
